import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, Grid, Stack, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, MenuItem,
    Avatar, InputAdornment, LinearProgress, IconButton, Tooltip
} from '@mui/material';
import {
    AccountBalanceWallet, AddCircle, TrendingUp, TrendingDown,
    CalendarMonth, Edit, Delete, ReceiptLong, AutoAwesome,
    Inventory, CheckCircleOutline, Visibility
} from '@mui/icons-material';
import {
    collection, getDocs, getDoc, query, where, orderBy,
    doc, Timestamp, updateDoc, addDoc, runTransaction
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from 'sweetalert2';

const PRIMARY = "#0d9488"; // Teal 600
const SECONDARY = "#0f172a"; // Slate 900

function Studentwallet() {
    const [students, setStudents] = useState([]);
    const [products, setProducts] = useState([]); // Needed to show item names
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [history, setHistory] = useState([]);
    const [inventoryData, setInventoryData] = useState([]);
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(false);

    // Modals state
    const [openAdd, setOpenAdd] = useState(false);
    const [viewRecord, setViewRecord] = useState(null); // State for viewing inventory details

    const [schoolId, setSchoolId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedTxnId, setSelectedTxnId] = useState(null);

    const [formData, setFormData] = useState({
        amount: '',
        receiptNo: '',
        bookReceiptNo: '',
        description: 'Wallet Top-up',
        payMode: 'Cash',
        txnDate: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        const init = async () => {
            if (!auth.currentUser?.uid) return;
            try {
                setLoading(true);
                const userDoc = await getDoc(doc(db, "users", auth.currentUser?.uid));
                const sId = userDoc.data()?.schoolId;
                setSchoolId(sId);

                if (sId) {
                    const yearSnap = await getDocs(query(collection(db, "academicyears"), where("schoolId", "==", sId), where("status", "==", "Active")));
                    const activeYear = yearSnap.empty ? null : yearSnap.docs[0].data().name;
                    if (activeYear) {
                        await fetchData(sId, activeYear);
                    }
                }
            } catch (e) {
                console.error("Initialization Error: ", e);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    const fetchData = async (sId, year) => {
        try {
            const [courseSnap, admSnap, stdSnap, pSnap] = await Promise.all([
                getDocs(query(collection(db, "courses"), orderBy("sequence", "asc"))),
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("academicYear", "==", year), where('status', "==", 'Active'))),
                getDocs(collection(db, "students")),
                getDocs(query(collection(db, "products"), where("schoolId", "==", sId))) // Fetch products
            ]);

            const courseMap = {};
            courseSnap.forEach(d => { courseMap[d.id] = d.data().name; });

            const admMap = {};
            admSnap.forEach(d => { admMap[d.data().studentId] = d.data(); });

            const merged = stdSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(s => admMap[s.id])
                .map(s => ({
                    ...s,
                    label: `${s.firstname || ''} ${s.fathername || ''} ${s.surname || ''} (${courseMap[admMap[s.id].class] || ''})`.replace(/\s+/g, ' ').toUpperCase()
                }));
            setStudents(merged);

            // Store products to map item names in the View popup
            setProducts(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error("Fetch Data Error: ", e);
        }
    };

    const fetchWalletHistory = async (student) => {
        if (!student) {
            setHistory([]);
            setBalance(0);
            return;
        }
        setLoading(true);
        try {
            const q = query(collection(db, "wallet_transactions"), where("studentId", "==", student.id), orderBy("timestamp", "desc"));
            const snap = await getDocs(q);
            const txns = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const total = txns.reduce((acc, curr) => curr.type === 'Credit' ? acc + Number(curr.amount) : acc - Number(curr.amount), 0);
            setHistory(txns);
            setBalance(total);
        } catch (e) {
            console.error("Fetch History Error: ", e);
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentInventory = async (student) => {
        if (!student) {
            setInventoryData([]);
            return;
        }
        try {
            const q = query(collection(db, "studentInventory"), where("studentId", "==", student.id), orderBy("updatedAt", "desc"));
            const snap = await getDocs(q);
            const invList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setInventoryData(invList);
        } catch (e) {
            console.error("Fetch Inventory Error: ", e);
        }
    };

    const handleOpenAddModal = () => {
        setIsEditMode(false);
        setSelectedTxnId(null);
        setFormData({
            amount: '',
            receiptNo: 'Generating on Submit...',
            bookReceiptNo: '',
            description: 'Wallet Top-up',
            payMode: 'Cash',
            txnDate: new Date().toISOString().split('T')[0]
        });
        setOpenAdd(true);
    };

    const handleOpenEditModal = (row) => {
        setIsEditMode(true);
        setSelectedTxnId(row.id);
        let formattedDate = new Date().toISOString().split('T')[0];
        if (row.timestamp) {
            const d = row.timestamp.toDate ? row.timestamp.toDate() : new Date(row.timestamp);
            formattedDate = d.toISOString().split('T')[0];
        }
        setFormData({
            amount: row.amount || '',
            receiptNo: row.receiptNo || '',
            bookReceiptNo: row.bookReceiptNo || '',
            description: row.description || '',
            payMode: row.payMode || 'Cash',
            txnDate: formattedDate
        });
        setOpenAdd(true);
    };

    const handleSaveTransaction = async () => {
        if (loading || !formData.amount || !selectedStudent) return;
        setLoading(true);
        try {
            const chosenDate = new Date(formData.txnDate);
            const counterRef = doc(db, "system_counters", `wallet_receipts_${schoolId}`);
            const txnsCollectionRef = collection(db, "wallet_transactions");

            if (isEditMode && selectedTxnId) {
                const txnData = {
                    studentId: selectedStudent.id,
                    studentName: selectedStudent.label,
                    schoolId: schoolId,
                    amount: Number(formData.amount),
                    receiptNo: formData.receiptNo,
                    bookReceiptNo: formData.bookReceiptNo || '',
                    type: 'Credit',
                    description: formData.description,
                    payMode: formData.payMode,
                    timestamp: Timestamp.fromDate(chosenDate),
                    dateTime: formData.txnDate
                };
                await updateDoc(doc(db, "wallet_transactions", selectedTxnId), txnData);
                Swal.fire({ icon: 'success', title: 'Transaction Updated', text: 'Financial row overwritten.', timer: 2000, showConfirmButton: false });
            } else {
                await runTransaction(db, async (transaction) => {
                    const counterDoc = await transaction.get(counterRef);
                    let nextNum = 1001;
                    if (counterDoc.exists()) {
                        nextNum = counterDoc.data().currentSeq + 1;
                    }
                    const finalReceiptNo = `REC-${nextNum}`;
                    const newTxnRef = doc(txnsCollectionRef);

                    transaction.set(newTxnRef, {
                        studentId: selectedStudent.id,
                        studentName: selectedStudent.label,
                        schoolId: schoolId,
                        amount: Number(formData.amount),
                        receiptNo: finalReceiptNo,
                        bookReceiptNo: formData.bookReceiptNo || '',
                        type: 'Credit',
                        description: formData.description,
                        payMode: formData.payMode,
                        timestamp: Timestamp.fromDate(chosenDate),
                        dateTime: formData.txnDate
                    });
                    transaction.set(counterRef, { currentSeq: nextNum }, { merge: true });
                });
                Swal.fire({ icon: 'success', title: 'Funds Added', text: `Receipt logged successfully.`, timer: 2000, showConfirmButton: false });
            }
            setOpenAdd(false);
            fetchWalletHistory(selectedStudent);
        } catch (e) {
            Swal.fire("System Error", e.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTransaction = async (row) => {
        const { value: reason } = await Swal.fire({
            title: 'Request Deletion Approval?',
            text: `Transaction deletion for receipt ${row.receiptNo || 'N/A'} requires administrator clearance.`,
            icon: 'warning',
            input: 'text',
            inputPlaceholder: 'Enter reason for deletion...',
            showCancelButton: true,
            confirmButtonColor: '#e11d48',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Submit Request',
            inputValidator: (value) => {
                if (!value) {
                    return 'You must supply a structural reason for deletion audit verification!';
                }
            }
        });

        if (reason) {
            setLoading(true);
            try {
                await addDoc(collection(db, "wallet_approvals"), {
                    transactionId: row.id,
                    studentId: selectedStudent.id,
                    studentName: selectedStudent.label,
                    schoolId: schoolId,
                    amount: row.amount,
                    receiptNo: row.receiptNo || '',
                    bookReceiptNo: row.bookReceiptNo || '',
                    type: "DELETE_TRANSACTION",
                    status: "Pending",
                    requestedBy: auth.currentUser?.email || "Staff Portal",
                    reason: reason,
                    createdAt: Timestamp.now()
                });

                Swal.fire({
                    icon: 'info',
                    title: 'Approval Pending',
                    text: 'Your deletion request has been successfully dispatched to the admin dashboard workspace.',
                    confirmButtonColor: PRIMARY
                });
            } catch (e) {
                Swal.fire("Error dispatching ticket", e.message, "error");
            } finally {
                setLoading(false);
            }
        }
    };

    const formatInventoryDate = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}-${month}-${year}`;
    };

    return (
        <Box p={{ xs: 2, md: 2 }} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, bgcolor: '#ccfbf1', '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} mb={4}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: SECONDARY, letterSpacing: '-0.5px' }}>
                        Student <span style={{ color: PRIMARY }}>Wallet Workspace</span>
                    </Typography>
                    <Typography variant="caption" color="textSecondary">Manage records, inventory, and request deletions securely</Typography>
                </Box>
                <Autocomplete
                    size="small"
                    options={students}
                    sx={{ width: { xs: '100%', sm: 350 }, bgcolor: 'white', borderRadius: '10px', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}
                    getOptionLabel={(o) => o.label}
                    onChange={(e, v) => {
                        setSelectedStudent(v);
                        fetchWalletHistory(v);
                        fetchStudentInventory(v);
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="Search Student Identity..." variant="outlined" />}
                />
            </Stack>

            <Grid container spacing={3}>
                {selectedStudent && (
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{
                            p: 3, borderRadius: '20px', display: 'flex', flexDirection: 'column',
                            justifyContent: 'space-between', background: `linear-gradient(135deg, ${SECONDARY} 0%, #1e293b 100%)`,
                            color: 'white', boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.15)', height: '100%', minHeight: '170px'
                        }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: 'rgba(13, 148, 136, 0.15)', width: 56, height: 56, border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <AccountBalanceWallet sx={{ color: PRIMARY, fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ opacity: 0.6, textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 600 }}>
                                        Current Balance
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 900, mt: 0.5 }}>₹{balance.toLocaleString('en-IN')}</Typography>
                                </Box>
                            </Stack>
                            <Button
                                variant="contained" startIcon={<AddCircle />} fullWidth
                                sx={{
                                    bgcolor: PRIMARY, borderRadius: '12px', py: 1.2, mt: 3, textTransform: 'none', fontWeight: 600,
                                    '&:hover': { bgcolor: '#0b7a6f', boxShadow: '0 4px 12px rgba(13, 148, 136, 0.3)' }
                                }}
                                onClick={handleOpenAddModal}
                            >
                                Process New Top-up
                            </Button>
                        </Paper>
                    </Grid>
                )}

                <Grid item xs={12} md={selectedStudent ? 8 : 12}>
                    <Stack spacing={3}>
                        {/* 1. Wallet History Section */}
                        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}>
                            <Box p={2.5} sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #f1f5f9' }}>
                                <Typography variant="subtitle1" fontWeight="700" color={SECONDARY}>Transaction Log Registry</Typography>
                            </Box>
                            <TableContainer sx={{ maxHeight: selectedStudent ? '45vh' : '65vh', bgcolor: 'white' }}>
                                <Table stickyHeader size="medium">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Date/Time</TableCell>
                                            <TableCell sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Receipt Numbers</TableCell>
                                            <TableCell sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Details</TableCell>
                                            <TableCell sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Mode</TableCell>
                                            <TableCell align="right" sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Amount</TableCell>
                                            <TableCell align="center" sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {history.map((row) => (
                                            <TableRow key={row.id} hover>
                                                <TableCell sx={{ fontSize: '0.825rem', color: '#64748b', fontWeight: 500 }}>{row.dateTime}</TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        {row.receiptNo && <Chip size="small" variant="contained" label={row.receiptNo} sx={{ borderRadius: '6px', fontWeight: 700, fontSize: '10px', color: '#ffffff', bgcolor: PRIMARY }} />}
                                                        {row.bookReceiptNo && <Chip size="small" variant="outlined" label={`Manual: ${row.bookReceiptNo}`} sx={{ borderRadius: '6px', fontWeight: 600, fontSize: '10px' }} />}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        {row.type === 'Credit' ?
                                                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#dcfce7' }}><TrendingUp sx={{ color: '#16a34a', fontSize: 16 }} /></Avatar> :
                                                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#ffe4e6' }}><TrendingDown sx={{ color: '#dc2626', fontSize: 16 }} /></Avatar>
                                                        }
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="600" color={SECONDARY}>{row.description}</Typography>
                                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>{row.type}</Typography>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell><Chip size="small" label={row.payMode || 'Auto'} sx={{ fontSize: '11px', fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }} /></TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" sx={{ fontWeight: '700', color: row.type === 'Credit' ? '#059669' : '#e11d48' }}>
                                                        {row.type === 'Credit' ? '+' : '-'} ₹{row.amount?.toLocaleString('en-IN')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                                        <IconButton size="small" onClick={() => handleOpenEditModal(row)} sx={{ color: '#0284c7' }}><Edit fontSize="small" /></IconButton>
                                                        <IconButton size="small" onClick={() => handleDeleteTransaction(row)} sx={{ color: '#dc2626' }}><Delete fontSize="small" /></IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {!selectedStudent && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                                    <Stack spacing={1} alignItems="center">
                                                        <AccountBalanceWallet sx={{ fontSize: 48, color: '#94a3b8' }} />
                                                        <Typography variant="body2" color="textSecondary">Please select a student above to initialize</Typography>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {selectedStudent && history.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 5, color: '#64748b' }}>No wallet transactions found.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* 2. Student Inventory Section */}
                        {selectedStudent && (
                            <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}>
                                <Box p={2.5} sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Inventory sx={{ color: PRIMARY }} />
                                    <Typography variant="subtitle1" fontWeight="700" color={SECONDARY}>Student Inventory Records</Typography>
                                </Box>
                                <TableContainer sx={{ maxHeight: '40vh', bgcolor: 'white' }}>
                                    <Table stickyHeader size="medium">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Issue Date</TableCell>
                                                <TableCell sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Total Items</TableCell>
                                                <TableCell sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Payment Mode</TableCell>
                                                <TableCell align="right" sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Grand Total</TableCell>
                                                <TableCell align="center" sx={{ color: '#64748b', fontWeight: 700, bgcolor: '#f8fafc' }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {inventoryData.map((inv) => (
                                                <TableRow key={inv.id} hover>
                                                    <TableCell sx={{ fontSize: '0.825rem', color: '#64748b', fontWeight: 500 }}>
                                                        {formatInventoryDate(inv.issueDate)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip size="small" icon={<CheckCircleOutline fontSize="small" />} label={`${inv.items?.length || 0} Items`} sx={{ fontWeight: 600, bgcolor: '#f1f5f9' }} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={inv.payMode || 'N/A'}
                                                            sx={{
                                                                fontSize: '11px', fontWeight: 700,
                                                                color: inv.payMode === 'Wallet' ? '#ffffff' : '#475569',
                                                                bgcolor: inv.payMode === 'Wallet' ? '#6366f1' : '#e2e8f0'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" sx={{ fontWeight: '700', color: SECONDARY }}>
                                                            ₹{inv.grandTotal?.toLocaleString('en-IN')}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="View Items & Photo">
                                                            <IconButton size="small" onClick={() => setViewRecord(inv)} sx={{ color: '#0ea5e9' }}>
                                                                <Visibility fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {inventoryData.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{ py: 5, color: '#64748b' }}>No inventory records found for this student.</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </Stack>
                </Grid>
            </Grid>

            {/* Wallet Add/Edit Transaction Dialog */}
            <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
                    <Typography variant="h6" fontWeight="800" color={SECONDARY}>
                        {isEditMode ? 'Update Transaction Log' : 'Create Top-up Transaction'}
                    </Typography>
                    <Typography variant="body2" color={PRIMARY} fontWeight="600" sx={{ mt: 0.5, wordBreak: 'break-word' }}>{selectedStudent?.label}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} mt={2}>
                        <TextField fullWidth label="Receipt No" type="text" InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start"><AutoAwesome sx={{ color: PRIMARY, fontSize: 20 }} /></InputAdornment> }} value={formData.receiptNo} />
                        <TextField fullWidth label="Book Receipt No (Manual Entry)" type="text" value={formData.bookReceiptNo} onChange={(e) => setFormData({ ...formData, bookReceiptNo: e.target.value })} />
                        <TextField fullWidth label="Recharge Amount Value" type="number" autoFocus={!isEditMode} value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
                        <TextField fullWidth label="Transaction Date" type="date" value={formData.txnDate} onChange={(e) => setFormData({ ...formData, txnDate: e.target.value })} InputLabelProps={{ shrink: true }} />
                        <TextField select fullWidth label="Payment Gateway Mode" value={formData.payMode} onChange={(e) => setFormData({ ...formData, payMode: e.target.value })}><MenuItem value="Cash">Cash Ledger</MenuItem><MenuItem value="Online">Online Banking / UPI</MenuItem><MenuItem value="Cheque">Bank Cheque Standard</MenuItem></TextField>
                        <TextField fullWidth label="Audit Remark Log" multiline rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
                    <Button onClick={() => setOpenAdd(false)} fullWidth sx={{ color: '#64748b', fontWeight: 600 }}>Discard</Button>
                    <Button variant="contained" fullWidth disabled={loading || !formData.amount} sx={{ bgcolor: PRIMARY, height: '46px', borderRadius: '12px', '&:hover': { bgcolor: '#0b7a6f' } }} onClick={handleSaveTransaction}>{loading ? 'Processing...' : isEditMode ? 'Commit Update' : 'Confirm Setup'}</Button>
                </DialogActions>
            </Dialog>

            {/* View Inventory Items & Photo Record Dialog */}
            <Dialog open={Boolean(viewRecord)} onClose={() => setViewRecord(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
                <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1, fontWeight: 'bold' }}>
                    Inventory Request Details
                </DialogTitle>
                <DialogContent dividers>
                    {viewRecord && (
                        <Box>
                            {/* Summary Grid */}
                            <Grid container spacing={2} mb={3}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Issue Date</Typography>
                                    <Typography variant="subtitle2" fontWeight="bold">{formatInventoryDate(viewRecord.issueDate)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Payment Mode</Typography>
                                    <Chip label={viewRecord.payMode} size="small" color={viewRecord.payMode === 'Wallet' ? 'secondary' : 'default'} />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Grand Total</Typography>
                                    <Typography variant="subtitle1" fontWeight="bold" color="primary">₹{viewRecord.grandTotal}</Typography>
                                </Grid>
                            </Grid>

                            <Typography variant="overline" color="textSecondary" fontWeight="bold">Issued Items Checklist</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell>Qty</TableCell>
                                            <TableCell>Rate</TableCell>
                                            <TableCell>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {viewRecord.items?.map((item, idx) => {
                                            // Matches product ID against fetched products to show standard name
                                            const product = products.find(p => p.id === item.productId);
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ fontWeight: 500 }}>{product ? product.productName : 'Unknown Product'}</TableCell>
                                                    <TableCell>{item.qty}</TableCell>
                                                    <TableCell>₹{item.rate}</TableCell>
                                                    <TableCell>₹{item.total}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Conditional Render for Photo URL */}
                            {viewRecord.photoUrl ? (
                                <Box textAlign="center" mt={2}>
                                    <Typography variant="overline" color="textSecondary" fontWeight="bold" display="block" mb={1}>
                                        Photo Evidence
                                    </Typography>
                                    <img
                                        src={viewRecord.photoUrl}
                                        alt="Student Inventory Evidence"
                                        style={{ maxWidth: '100%', maxHeight: '320px', borderRadius: '12px', border: '1px solid #e2e8f0' }}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary" fontStyle="italic" textAlign="center" sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                                    No photo evidence was attached to this specific record.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setViewRecord(null)} variant="outlined" sx={{ borderRadius: '10px' }}>Close Window</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Studentwallet;